/**
 * =============================================================================
 * VisibilityBadge - Selo compacto para indicar o modo de visibilidade
 * Versão: 1.0.0 (2026-06-25)
 *
 * Pequeno badge usado nas listagens (Listas, Campanhas, Templates) para
 * sinalizar rapidamente quem pode ver o registro.
 * =============================================================================
 */
import { Lock, Users, Tags, Globe2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { EntityVisibility } from '@/hooks/useEntityVisibility';

interface VisibilityBadgeProps {
  visibility: EntityVisibility | null | undefined;
  className?: string;
  /** Quando true, mostra somente o ícone (sem texto). */
  iconOnly?: boolean;
}

const META: Record<EntityVisibility, { label: string; icon: React.ComponentType<{ className?: string }>; tone: string }> = {
  private:    { label: 'Privada',       icon: Lock,    tone: 'bg-muted text-muted-foreground' },
  shared:     { label: 'Compartilhada', icon: Users,   tone: 'bg-blue-500/10 text-blue-700 dark:text-blue-300' },
  by_profile: { label: 'Por perfil',    icon: Tags,    tone: 'bg-amber-500/10 text-amber-700 dark:text-amber-300' },
  public:     { label: 'Todos',         icon: Globe2,  tone: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' },
};

export default function VisibilityBadge({ visibility, className, iconOnly }: VisibilityBadgeProps) {
  if (!visibility) return null;
  const meta = META[visibility] ?? META.public;
  const Icon = meta.icon;
  return (
    <Badge
      variant="secondary"
      className={cn(
        'gap-1 font-normal border-transparent',
        meta.tone,
        className,
      )}
      title={meta.label}
    >
      <Icon className="h-3 w-3" />
      {!iconOnly && <span className="text-[11px]">{meta.label}</span>}
    </Badge>
  );
}
